import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Target, TrendingDown, TrendingUp, Minus, Check, X, RefreshCw, Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdaptiveCalorieTarget = ({ token, onTargetChanged }) => {
  const { t } = useTranslation();
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    fetchRecommendation();
  }, [token]);

  const fetchRecommendation = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/calorie-target/adaptive`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendation(res.data);
    } catch (err) {
      console.error('Error fetching adaptive target:', err);
    } finally {
      setLoading(false);
    }
  };

  const acceptTarget = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/calorie-target/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccepted(true);
      onTargetChanged?.();
    } catch (err) {
      console.error('Error accepting target:', err);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4 text-gray-500 dark:text-gray-400" data-testid="adaptive-loading">{t('common.loading')}</div>;
  }

  if (!recommendation) return null;

  const { current_target, suggested_target, adjustment, reason, weight_trend, avg_daily_intake, weight_data_points, weekly_change } = recommendation;
  const hasAdjustment = adjustment !== 0;

  const trendIcon = weight_trend === 'losing' ? <TrendingDown size={18} className="text-green-500" /> :
                    weight_trend === 'gaining' ? <TrendingUp size={18} className="text-red-500" /> :
                    <Minus size={18} className="text-gray-500 dark:text-gray-400" />;

  return (
    <div className="space-y-4" data-testid="adaptive-calorie-target">
      <div className="flex items-center gap-2 mb-2">
        <Target className="text-[#FF6600]" size={24} />
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">{t('adaptive.title')}</h2>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{t('adaptive.subtitle')}</p>

      {/* Current vs Suggested */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('adaptive.currentTarget')}</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-white" data-testid="current-target">{Math.round(current_target)}</p>
          <p className="text-xs text-gray-400">{t('adaptive.calPerDay')}</p>
        </div>
        <div className={`rounded-xl p-4 border text-center ${
          hasAdjustment
            ? 'bg-[#FF6600]/5 border-[#FF6600]/30'
            : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        }`}>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('adaptive.suggestedTarget')}</p>
          <p className={`text-2xl font-bold ${hasAdjustment ? 'text-[#FF6600]' : 'text-green-600'}`} data-testid="suggested-target">
            {Math.round(suggested_target)}
          </p>
          <p className="text-xs text-gray-400">
            {adjustment > 0 ? `+${adjustment}` : adjustment < 0 ? adjustment : '='} {t('adaptive.calPerDay')}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-center gap-1 mb-1">
            {trendIcon}
          </div>
          <p className="text-sm font-semibold text-gray-800 dark:text-white">
            {weight_data_points >= 3 ? `${weekly_change > 0 ? '+' : ''}${weekly_change}kg` : '--'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('adaptive.weeklyChange')}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-gray-100 dark:border-gray-700">
          <Scale size={18} className="mx-auto mb-1 text-blue-500" />
          <p className="text-sm font-semibold text-gray-800 dark:text-white">{weight_data_points}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('adaptive.weightLogs')}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-gray-100 dark:border-gray-700">
          <Target size={18} className="mx-auto mb-1 text-purple-500" />
          <p className="text-sm font-semibold text-gray-800 dark:text-white">{avg_daily_intake || '--'}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('adaptive.avgIntake')}</p>
        </div>
      </div>

      {/* Recommendation Reason */}
      <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
        <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="adaptive-reason">{reason}</p>
      </div>

      {/* Accept/Dismiss */}
      {hasAdjustment && !accepted && (
        <div className="flex gap-2">
          <button
            data-testid="accept-target-btn"
            onClick={acceptTarget}
            disabled={accepting}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#FF6600] text-white rounded-lg font-semibold hover:bg-[#E55C00] disabled:opacity-50 transition-colors"
          >
            <Check size={18} />
            {accepting ? t('common.loading') : t('adaptive.acceptTarget')}
          </button>
          <button
            data-testid="dismiss-target-btn"
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={18} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      )}

      {accepted && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-green-700 dark:text-green-300 text-sm" data-testid="target-accepted">
          <Check size={16} />
          {t('adaptive.targetAccepted')}
        </div>
      )}
    </div>
  );
};

export default AdaptiveCalorieTarget;
